import React, { useState, useEffect, useRef } from 'react'
import { Checkbox, FormControlLabel } from '@material-ui/core'

import BuilderMapView from '../component/BuilderMapView'
import DrawMap from '../../../Components/DrawMap/container/DrawMap'

import {
  getFeatureFromOverlay,
  getOverlayColorAndOpacity,
  getFormatedArea,
} from '../util'
import {
  addFeaturesPFGQL,
  updateFeaturesPFGQL,
  deleteFeaturesPFGQL,
} from '../../../Module/graphQL/PathFinder/pathFinderGQL'
import { debounce } from '../../../Utils/Functions'
import { BUILDER_MAP_MODE } from '../const'
import PostcodesSearch from '../../../Components/PostcodesSearch/component/PostcodesSearch'
import { showReduxModal } from '../../../Reducers/UI/actions'
import { connect } from 'react-redux'
import { createOverlayName } from '../../../Utils/Map'
import { checkIfOverlaysIntersect } from '../util'
import useOverlaysFetch from '../../../Utils/Hooks/useOverlaysFetch'
import { SelectDashboard } from '../../../Components/Form'
import Row from 'react-bootstrap/lib/Row'
import Col from 'react-bootstrap/lib/Col'
import { FILTER_MAP_DENSITY_ENUM } from 'shared/constants'

let requestId = null

/**
 * Component that renders map that can be used to create polygons and save the against PF in EDIT mode
 * And to toogle existing overlays in the TOGGLE mode
 * @param {string} props.countryCode Country code that will be used in the PF request
 * @param {object} props.mapPosition Map's center
 * @param {object} props.defaultMapPosition Initial map's center
 * @param {string} props.mode Builder map's mode (edit|toggle)
 * @param {function} props.onOverlayClick Will be called on overlay click
 * @param {function} props.processOverlays Lets us encapsulate BuilderMap with only minimal needed logic and keep it highly reusable and flexible
 * Applies specific logic from the parent and returns processed overlays
 * @param {function} props.showReduxModal Show modal window. Added via `connect()` wrapper
 *
 * @returns {JSX.Element} <BuilderMap />
 */
const BuilderMap = ({
  countryCode,
  mapPosition,
  defaultMapPosition,
  mode = BUILDER_MAP_MODE.EDIT,
  onOverlayClick = () => {},
  processOverlays = o => o,
  setModalControls,
  showReduxModal,
}) => {
  const [renderedOverlays, setRenderedOverlays] = useState([])
  const [visiblePostcodes, setVisiblePostcodes] = useState([])
  const [postcodes, setPostcodes] = useState({})
  const [missionBoundaries, setMissionBoundaries] = useState({})
  const [loading, setLoading] = useState(false)
  const [map, setMap] = useState(false)
  const [isDrawing, setIsDrawing] = useState(false)
  const drawingStatusRef = useRef()
  const renderedOverlaysRef = useRef()
  // FR Specific
  const [isIRISVisible, setIsIRISVisibility] = useState(true)
  const [isMissionsVisible, setIsMisionsVisibility] = useState(true)
  const [selectedFilter, setSelectedFilter] = useState(
    FILTER_MAP_DENSITY_ENUM.ALL,
  )
  const [densityFilterLoaded, setDensityFilterLoaded] = useState(false)

  const filterOptions = Object.values(FILTER_MAP_DENSITY_ENUM).map(item => ({
    label: item,
    value: item,
  }))

  const { loadData } = useOverlaysFetch({ mode, countryCode, processOverlays })

  useEffect(() => {
    renderedOverlaysRef.current = renderedOverlays
  }, [renderedOverlays])
  const MAX_ZOOM = 14
  const shouldShowAdditionalLayer = ['FR', 'BE'].includes(countryCode)
  /**
   * Updates map center
   */
  useEffect(() => {
    if (!map) return
    setSelectedFilter(FILTER_MAP_DENSITY_ENUM.ALL)
    map.setCenter(mapPosition)
    map.setZoom(12)
  }, [mapPosition])

  /**
   * Update visible overlays
   */
  useEffect(() => {
    if (shouldShowAdditionalLayer && isMissionsVisible) {
      const keyPerContry = {
        'FR': 'codeIRIS',
        'BE': 'statisticalSectorCode',
      }
      const key = keyPerContry[countryCode]

      setRenderedOverlays([
        ...Object.values(missionBoundaries).filter(boundary => {
          if (
            !boundary.metadata ||
            [undefined, null].includes(boundary.metadata[key])
          ) {
            return true
          }
          try {
            if (isIRISVisible) {
              return boundary.metadata[key]
            } else {
              return !boundary.metadata[key]
            }
          } catch (error) {
            console.warn(error)
            return true
          }
        }),
        ...Object.values(postcodes).filter(overlay =>
          visiblePostcodes.includes(overlay.name),
        ),
      ])
    } else {
      isMissionsVisible
        ? setRenderedOverlays([
            ...Object.values(missionBoundaries),
            ...Object.values(postcodes).filter(overlay =>
              visiblePostcodes.includes(overlay.name),
            ),
          ])
        : setRenderedOverlays([
            ...Object.values(postcodes).filter(overlay =>
              visiblePostcodes.includes(overlay.name),
            ),
          ])
    }
  }, [
    missionBoundaries,
    postcodes,
    visiblePostcodes,
    isIRISVisible,
    isMissionsVisible,
  ])

  /**
   * Resolves stale state issue for onBoundsChanged
   */
  useEffect(() => {
    drawingStatusRef.current = isDrawing
  }, [isDrawing])
  /**
   * Adds listener so we know when viewbox is changed
   * @param {GMAP} map
   */
  const onMapInit = (newMap, setModalVisible) => {
    if (!newMap || map) return
    newMap.addListener(
      'bounds_changed',
      // Using debounce (1s) so we wont spam expensive requests
      debounce(() => onBoundsChanged(newMap), 1000),
    )
    onBoundsChanged(newMap)
    setMap(newMap)
    setModalControls && setModalControls(setModalVisible)
  }

  /**
   * Handles viewbox updates by fetching new overlays for new bounds
   * It wont request households amount if view area is too large for PF to handle
   * @param {GMAP} map
   */
  const onBoundsChanged = async map => {
    // Don't fetch and trigger re-render if user is drawing
    if (drawingStatusRef.current) return
    try {
      const latLngBound = map.getBounds()
      const zoom = map.getZoom()
      if (!latLngBound) return
      const formattedArea = await getFormatedArea(latLngBound)
      setLoading(true)
      const includeAddressCount = zoom > MAX_ZOOM
      const currentRequestId = Date.now()
      requestId = currentRequestId

      const postcodes = await loadData({
        layer: 'Postcodes',
        area: formattedArea,
        includeAddressCount,
      })

      if (currentRequestId !== requestId) {
        //Race condition occurred
        return
      }

      setPostcodes(postcodes.data)

      const fetchMissionBoundaries = async ({
        layer = 'MissionBoundaries',
        after,
      }) => {
        const boundaries = await loadData({
          layer,
          area: formattedArea,
          includeAddressCount,
          after,
        })

        setMissionBoundaries(previousState => ({
          ...previousState,
          ...boundaries.data,
        }))

        if (boundaries.pageInfo.hasNextPage) {
          fetchMissionBoundaries({
            layer,
            after: boundaries.pageInfo.endCursor,
          })
        }
      }

      if (countryCode === 'FR') {
        fetchMissionBoundaries({ layer: 'IRISBoundaries' })
      } else if (countryCode === 'BE') {
        fetchMissionBoundaries({ layer: 'BEStatisticalSectors' })
      }
      fetchMissionBoundaries({ layer: 'MissionBoundaries' })
    } catch (error) {
      console.error('PathFinder error.')
      throw error
    } finally {
      setLoading(false)
    }
  }
  /**
   * Save/update feature on the PF
   * @param {number} param.id Feature's id if already exists
   * @param {array} param.id Feature's boundary
   */
  const onOverlaySave = async ({ id, boundary, overlay }) => {
    if (
      checkIfOverlaysIntersect(
        boundary,
        renderedOverlaysRef.current.filter(({ isPostcode }) => !isPostcode),
        id,
      )
    ) {
      showReduxModal({
        title: 'Mission boundaries overlap',
        children:
          'This mission overlaps with an existing mission. Please move mission or edit boundary if you do not want them to overlap.',
        confirmLabel: 'OK',
        hideCancel: true,
      })
    }
    try {
      setLoading(true)

      const customName = overlay.payload && overlay.payload.label

      const label = customName
        ? customName
        : await createOverlayName({ countryCode, boundary, renderedOverlays })

      overlay.set('payload', { ...overlay.payload, label })

      if (id) {
        const feature = getFeatureFromOverlay({
          countryCode,
          boundary: boundary,
        })

        const input = {
          features: [
            {
              ...feature,
              properties: {
                id,
                name: label,
              },
            },
          ],
        }
        await updateFeaturesPFGQL(input)
        initOverlay({ featureId: id, boundary, label })
        setLoading(false)
        return { id }
      }
      const feature = getFeatureFromOverlay({
        countryCode,
        boundary: boundary,
      })
      const input = {
        features: [
          {
            ...feature,
            properties: {
              name: label,
            },
          },
        ],
      }
      const response = await addFeaturesPFGQL(input)
      const { data, errors } = await response.json()
      if (errors && errors[0]) throw new Error(errors[0].message)
      const featureId = data.addFeatures.features[0].properties.id
      initOverlay({ featureId, boundary, label })
      return { id: featureId }
    } finally {
      setLoading(false)
    }
  }

  const initOverlay = ({ featureId, boundary, label }) => {
    const isEdit = mode === BUILDER_MAP_MODE.EDIT
    const { color, fillOpacity, strokeOpacity } = getOverlayColorAndOpacity({
      isEdit,
      isPostcode: false,
    })
    const overlay = {
      id: featureId,
      name: label,
      color,
      mapPolygonProps: {
        key: featureId,
        disabled: !isEdit,
        canBeDeleted: isEdit,
        fillOpacity,
        strokeOpacity,
        payload: {
          label,
        },
      },
      paths: [
        boundary.map(({ latitude, longitude }) => ({
          lat: latitude,
          lng: longitude,
        })),
      ],
    }

    return setMissionBoundaries(missionBoundariesCurrent => {
      return { ...missionBoundariesCurrent, [featureId]: overlay }
    })
  }

  /**
   * Delete feature from the PF with confirmation modal
   * @param {number} param.customId Feature's id
   */
  const onOverlayDeleteConfirmation = overlays =>
    new Promise(resolve => {
      showReduxModal({
        title: 'Delete mission(s) area?',
        onConfirm: () => {
          onOverlayDelete(overlays)
          resolve()
        },
        confirmLabel: 'Delete',
      })
    })

  /**
   * Delete feature from the PF
   * @param {number} param.customId Feature's id
   */
  const onOverlayDelete = async overlays => {
    setLoading(true)
    const input = {
      features: overlays.map(overlay => ({
        layer: 'MissionBoundaries',
        properties: {
          id: overlay.customId,
        },
      })),
    }
    var temp = renderedOverlays.slice(0)
    temp = renderedOverlays.filter(a => !overlays.find(b => b.customId == a.id))

    setRenderedOverlays(temp)
    setMissionBoundaries(prevMissionBoundaries => {
      const clone = { ...prevMissionBoundaries }
      overlays.forEach(({ customId: deletedId }) => {
        delete clone[deletedId]
      })
      return clone
    })
    await deleteFeaturesPFGQL(input)
    setLoading(false)
  }

  /**
   * Save new active tags to local state
   * @param {array} activeTags Postcodes that were chosen by user
   */
  const onPostcodesUpdate = ({ activeTags }) => setVisiblePostcodes(activeTags)
  const getVisibleOverlaysMap = () =>
    renderedOverlays.reduce((acc, overlay) => {
      acc[overlay.id] = true
      return acc
    }, {})

  return (
    <BuilderMapView>
      {shouldShowAdditionalLayer ? (
        <FormControlLabel
          control={
            <Checkbox
              checked={isIRISVisible}
              onChange={event => setIsIRISVisibility(event.target.checked)}
              name="IRIS"
            />
          }
          label="Toggle IRIS visibility"
        />
      ) : null}
      <FormControlLabel
        control={
          <Checkbox
            checked={isMissionsVisible}
            onChange={event => setIsMisionsVisibility(event.target.checked)}
            name="missionsVisible"
          />
        }
        label="Toggle missions visibility"
      />
      <Row>
        <Col xs={4}>
          <SelectDashboard
            options={filterOptions}
            value={selectedFilter}
            onChange={(a, b, value) => {
              setSelectedFilter(value)
            }}
            disabled={!densityFilterLoaded}
          />
        </Col>
        <Col xs={8}>
          <PostcodesSearch
            countryCode={countryCode}
            onUpdate={onPostcodesUpdate}
          />
        </Col>
      </Row>

      <DrawMap
        onMapInit={onMapInit}
        onSave={onOverlaySave}
        initialOverlays={renderedOverlays}
        onDelete={onOverlayDeleteConfirmation}
        countryCode={countryCode}
        shouldPickWeek={false}
        loading={loading}
        onOverlayClick={onOverlayClick}
        hideMapControls={mode === BUILDER_MAP_MODE.TOGGLE}
        mapOptions={{
          center: mapPosition || defaultMapPosition,
        }}
        mapControls={['DRAW']}
        onDrawingChange={setIsDrawing}
        visibleOverlaysMap={getVisibleOverlaysMap()}
        filter={selectedFilter}
        onFilterLoaded={setDensityFilterLoaded}
      />
    </BuilderMapView>
  )
}

const mapDispatchToProps = { showReduxModal }

export default connect(
  null,
  mapDispatchToProps,
)(BuilderMap)



// WEBPACK FOOTER //
// ./src/Containers/BuilderMap/container/BuilderMap.js